import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';

// ─── Types ───────────────────────────────────────────────────────────────────
interface LineChecklistItem {
  id?: string;
  category?: string | null;
  title?: string | null;
  description?: string | null;
  status?: string | null;
  remarks?: string | null;
  evidence_images?: string[];
}

interface LineDefectItem {
  id?: string;
  type?: string | null;
  title?: string | null;
  description?: string | null;
  category?: string | null;
  severity?: string | null;
  count?: number | null;
  evidence_images?: string[];
}

interface LinePhotoItem {
  id?: string;
  image_url?: string | null;
  caption?: string | null;
  label?: string | null;
  is_required?: boolean;
  description?: string | null;
}

interface LineVerificationItem {
  name?: string | null;
  title?: string | null;
  value?: string | null;
  unit?: string | null;
  units?: string | null;
  measurement_unit?: string | null;
}

export interface LineReportPDFData {
  id: string;
  result?: string | null;
  status?: string | null;
  basis?: string | null;
  ref_number?: string | null;
  fabric_type?: string | null;
  fabric_type_name?: string | null;
  sampling_method?: string | null;
  lot_size?: number | null;
  sample_size?: number | null;
  inspector_name?: string | null;
  inspector_signature_url?: string | null;
  factory_rep_signature_url?: string | null;
  remarks?: string | null;
  created_at?: string | null;
  process?: { id?: string; name?: string | null } | null;
  checklists?: LineChecklistItem[];
  defects?: LineDefectItem[];
  photos?: LinePhotoItem[];
  verifications?: LineVerificationItem[];
  additional_documents?: string[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const dash = (v: any): string => {
  if (v === null || v === undefined) return '—';
  const str = String(v).trim();
  return str.length === 0 ? '—' : str;
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 64,
  },

  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  logoText: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#000000' },
  titleLine2: { fontSize: 9, color: '#555555' },
  hr: { borderBottomWidth: 1, borderBottomColor: '#000000', marginBottom: 10 },

  resultRow: { flexDirection: 'row', borderWidth: 1, borderColor: '#000000', marginBottom: 12 },
  resultBox: { paddingHorizontal: 12, paddingVertical: 8, borderRightWidth: 1, borderRightColor: '#aaaaaa', justifyContent: 'center', minWidth: 130 },
  resultLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2 },
  resultValue: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#000000' },
  statBox: { flex: 1, paddingVertical: 8, paddingHorizontal: 6, borderRightWidth: 0.5, borderRightColor: '#cccccc', alignItems: 'center', justifyContent: 'center' },
  statLabel: { fontSize: 7.5, color: '#666666', textAlign: 'center', marginBottom: 2 },
  statValue: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center' },

  sectionHeader: { backgroundColor: '#d9d9d9', paddingHorizontal: 8, paddingVertical: 5, marginBottom: 6, marginTop: 4 },
  sectionHeaderText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#000000' },

  subHeader: { backgroundColor: '#f0f0f0', paddingHorizontal: 6, paddingVertical: 3, marginBottom: 4, marginTop: 6 },
  subHeaderText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 },
  infoCell: { width: '33.33%', paddingRight: 8, marginBottom: 8 },
  infoCellHalf: { width: '50%', paddingRight: 8, marginBottom: 8 },
  infoLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2, textTransform: 'uppercase' },
  infoValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#000000' },

  table: { borderWidth: 0.5, borderColor: '#cccccc', marginBottom: 8 },
  tableHead: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 0.5, borderBottomColor: '#cccccc' },
  tableRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: '#eeeeee' },
  th: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 3 },
  td: { flex: 1, fontSize: 8.5, paddingHorizontal: 6, paddingVertical: 3 },

  checklistItem: { borderBottomWidth: 0.5, borderBottomColor: '#eeeeee', paddingVertical: 6, paddingHorizontal: 4 },
  checklistRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 3 },
  statusBadge: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 2, borderWidth: 0.5 },
  checklistTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#111111', flex: 1 },
  checklistDesc: { fontSize: 8, color: '#555555', marginBottom: 3, paddingLeft: 0 },
  checklistRemarks: { fontSize: 8, color: '#333333', fontStyle: 'italic', marginTop: 2 },

  evidenceRow: { flexDirection: 'row', gap: 6, marginTop: 4, flexWrap: 'wrap' },
  evidenceImg: { width: 80, height: 60, objectFit: 'cover' },

  defectCard: { borderWidth: 0.5, borderColor: '#cccccc', padding: 6, marginBottom: 6, backgroundColor: '#fafafa' },
  defectRow: { flexDirection: 'row', gap: 12, marginBottom: 4 },
  defectField: { flex: 1 },
  defectFieldLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#888888', marginBottom: 1, textTransform: 'uppercase' },
  defectFieldValue: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#111111' },
  defectDesc: { fontSize: 8, color: '#444444', fontStyle: 'italic', marginTop: 3, marginBottom: 4 },

  verificationRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#eeeeee', paddingVertical: 5, paddingHorizontal: 4 },
  verificationName: { flex: 1, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#111111' },
  verificationValue: { fontSize: 9, color: '#333333', textAlign: 'right' },

  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  photoTile: { width: 120 },
  photoLabel: { fontSize: 7.5, color: '#333333', marginBottom: 3 },
  photoImg: { width: 120, height: 90, objectFit: 'cover' },

  remarksBlock: { borderWidth: 0.5, borderColor: '#cccccc', padding: 8, marginBottom: 8, backgroundColor: '#fafafa' },
  remarksLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#555555', marginBottom: 4, textTransform: 'uppercase' },
  remarksText: { fontSize: 9, color: '#222222', lineHeight: 1.4 },

  sigRow: { flexDirection: 'row', gap: 20, marginTop: 16 },
  sigBox: { flex: 1, borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 6 },
  sigLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#444444' },
  sigImg: { width: 110, height: 44, objectFit: 'contain', marginBottom: 4 },
  sigName: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  sigRole: { fontSize: 8, color: '#666666' },
  sigEmpty: { height: 44, marginBottom: 4 },

  noDataText: { fontSize: 8, color: '#888888', fontStyle: 'italic', paddingHorizontal: 4, paddingBottom: 6 },

  pageFooter: {
    position: 'absolute', bottom: 14, left: 36, right: 36,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerSide: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Shared sub-components ────────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <Text style={s.logoText}>QA MATE</Text>
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>LINE INSPECTION REPORT</Text>
        <Text style={s.titleLine2}>QUALITY ASSURANCE DEPARTMENT</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerSide} />
    <Text style={s.footerCenter} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    <Text style={s.footerRight}>QAMATE</Text>
  </View>
);

const SectionBar = ({ title }: { title: string }) => (
  <View style={s.sectionHeader}>
    <Text style={s.sectionHeaderText}>{title}</Text>
  </View>
);

const SubBar = ({ title }: { title: string }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

const InfoCell = ({ label, value, half }: { label: string; value: string; half?: boolean }) => (
  <View style={half ? s.infoCellHalf : s.infoCell}>
    <Text style={s.infoLabel}>{label}</Text>
    <Text style={s.infoValue}>{value}</Text>
  </View>
);

// ─── Main PDF Document ────────────────────────────────────────────────────────
const LineInspectionReportPDF = ({ inspection }: { inspection: LineReportPDFData }) => {
  const result = (inspection.result || 'PENDING').toUpperCase();
  const checklists = inspection.checklists || [];
  const defects = inspection.defects || [];
  const photos = inspection.photos || [];
  const verifications = inspection.verifications || [];

  const passCount = checklists.filter(c => (c.status || '').toUpperCase() === 'PASS').length;
  const totalDefects = defects.reduce((acc, d) => acc + (d.count || 0), 0);
  const requiredPhotos = photos.filter(p => p.is_required === true && p.image_url);
  const additionalPhotos = photos.filter(p => p.is_required !== true && p.image_url);

  const reportId = `QLI ${inspection.id.slice(0, 6).toUpperCase()}`;

  return (
    <Document title={`Line Inspection Report - ${reportId}`}>

      {/* ═══ PAGE 1 — Result + Inspection Details ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        <View style={s.resultRow}>
          <View style={s.resultBox}>
            <Text style={s.resultLabel}>LINE INSPECTION RESULT</Text>
            <Text style={s.resultValue}>{result}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Total Defects</Text>
            <Text style={s.statValue}>{totalDefects}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Checklist Score</Text>
            <Text style={s.statValue}>{passCount}/{checklists.length}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Sample Size</Text>
            <Text style={s.statValue}>{dash(inspection.sample_size)}</Text>
          </View>
          <View style={[s.statBox, { borderRightWidth: 0 }]}>
            <Text style={s.statLabel}>Report ID</Text>
            <Text style={s.statValue}>{reportId}</Text>
          </View>
        </View>

        <SectionBar title="INSPECTION DETAILS" />
        <View style={s.infoGrid}>
          <InfoCell label="Inspection ID" value={inspection.id.substring(0, 8).toUpperCase()} />
          <InfoCell label="PO / Batch / Shift" value={dash(inspection.basis)} />
          <InfoCell label="Production Process" value={dash(inspection.process?.name)} />
          <InfoCell label="Ref Number" value={dash(inspection.ref_number)} />
          <InfoCell label="Fabric Type" value={dash(inspection.fabric_type_name || inspection.fabric_type)} />
          <InfoCell label="Sampling Method" value={dash(inspection.sampling_method)} />
          <InfoCell label="Lot Size" value={inspection.lot_size != null ? inspection.lot_size.toLocaleString() : '—'} />
          <InfoCell label="Sample Size" value={dash(inspection.sample_size)} />
          <InfoCell label="Inspector" value={dash(inspection.inspector_name)} />
          <InfoCell label="Date" value={formatDate(inspection.created_at)} />
          <InfoCell label="Status" value={dash(inspection.status)} />
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 2 — Checklist ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />
        <SectionBar title={`CHECKLIST (${passCount} Passed · ${checklists.length - passCount} Failed)`} />
        {checklists.length === 0 && (
          <Text style={s.noDataText}>No checklist items recorded.</Text>
        )}
        {checklists.map((item, idx) => {
          const st = (item.status || '').toUpperCase();
          const isPass = st === 'PASS';
          const isFail = st === 'FAIL';
          const color = isPass ? '#16a34a' : isFail ? '#dc2626' : '#64748b';
          const evidenceImgs = (item.evidence_images || []).filter(Boolean);
          return (
            <View key={idx} style={s.checklistItem} wrap={false}>
              <View style={s.checklistRow}>
                <Text style={[s.checklistTitle, { marginRight: 6 }]}>{item.title || item.category || 'Item'}</Text>
                <Text style={[s.statusBadge, { borderColor: color, color }]}>{item.status?.toUpperCase() || 'N/A'}</Text>
              </View>
              {item.description ? <Text style={s.checklistDesc}>{item.description}</Text> : null}
              {item.remarks ? <Text style={s.checklistRemarks}>Remarks: {item.remarks}</Text> : null}
              {evidenceImgs.length > 0 && (
                <View style={s.evidenceRow}>
                  {evidenceImgs.map((url, i) => (
                    <Image key={i} src={url} style={s.evidenceImg} />
                  ))}
                </View>
              )}
            </View>
          );
        })}
        <PageFooter />
      </Page>

      {/* ═══ DEFECTS (conditional) ═══ */}
      {defects.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title={`RECORDED DEFECTS (Total: ${totalDefects})`} />
          {defects.map((defect, idx) => {
            const evidenceImgs = (defect.evidence_images || []).filter(Boolean);
            const sev = (defect.severity || '').toUpperCase();
            const sevColor = sev === 'CRITICAL' ? '#dc2626' : sev === 'MAJOR' ? '#d97706' : '#2563eb';
            return (
              <View key={idx} style={s.defectCard} wrap={false}>
                <View style={s.defectRow}>
                  <View style={s.defectField}>
                    <Text style={s.defectFieldLabel}>Title</Text>
                    <Text style={s.defectFieldValue}>{dash(defect.title)}</Text>
                  </View>
                  <View style={s.defectField}>
                    <Text style={s.defectFieldLabel}>Category</Text>
                    <Text style={s.defectFieldValue}>{dash(defect.category)}</Text>
                  </View>
                  <View style={s.defectField}>
                    <Text style={s.defectFieldLabel}>Type</Text>
                    <Text style={s.defectFieldValue}>{dash(defect.type)}</Text>
                  </View>
                  <View style={s.defectField}>
                    <Text style={s.defectFieldLabel}>Count</Text>
                    <Text style={s.defectFieldValue}>{dash(defect.count)}</Text>
                  </View>
                  <View style={s.defectField}>
                    <Text style={s.defectFieldLabel}>Severity</Text>
                    <Text style={[s.defectFieldValue, { color: sevColor }]}>{dash(defect.severity)}</Text>
                  </View>
                </View>
                {defect.description ? <Text style={s.defectDesc}>"{defect.description}"</Text> : null}
                {evidenceImgs.length > 0 && (
                  <View style={s.evidenceRow}>
                    {evidenceImgs.map((url, i) => (
                      <Image key={i} src={url} style={s.evidenceImg} />
                    ))}
                  </View>
                )}
              </View>
            );
          })}
          <PageFooter />
        </Page>
      )}

      {/* ═══ MEASUREMENTS & VERIFICATIONS (conditional) ═══ */}
      {verifications.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="MEASUREMENTS & VERIFICATIONS" />
          <View style={s.table}>
            <View style={s.tableHead}>
              <Text style={[s.th, { flex: 2 }]}>Measurement Item</Text>
              <Text style={s.th}>Value</Text>
            </View>
            {verifications.map((v, idx) => {
              const name = v.name || v.title || '—';
              const unit = v.unit || v.units || v.measurement_unit || '';
              const displayVal = v.value ? (unit ? `${v.value} ${unit}` : v.value) : '—';
              return (
                <View key={idx} style={s.tableRow}>
                  <Text style={[s.td, { flex: 2, fontFamily: 'Helvetica-Bold' }]}>{name}</Text>
                  <Text style={s.td}>{displayVal}</Text>
                </View>
              );
            })}
          </View>
          <PageFooter />
        </Page>
      )}

      {/* ═══ PHOTOS (conditional) ═══ */}
      {(requiredPhotos.length > 0 || additionalPhotos.length > 0) && (
        <Page size="A4" style={s.page}>
          <PageTop />
          {requiredPhotos.length > 0 && (
            <>
              <SectionBar title="INSPECTION PHOTOS" />
              <View style={s.photoGrid}>
                {requiredPhotos.map((p, i) => (
                  <View key={i} style={s.photoTile}>
                    <Text style={s.photoLabel}>{p.label || p.caption || 'Photo'}</Text>
                    <Image src={p.image_url!} style={s.photoImg} />
                  </View>
                ))}
              </View>
            </>
          )}
          {additionalPhotos.length > 0 && (
            <>
              <SubBar title="ADDITIONAL CAPTURES" />
              <View style={s.photoGrid}>
                {additionalPhotos.map((p, i) => (
                  <View key={i} style={s.photoTile}>
                    <Text style={s.photoLabel}>{p.label || p.caption || 'Photo'}</Text>
                    <Image src={p.image_url!} style={s.photoImg} />
                  </View>
                ))}
              </View>
            </>
          )}
          <PageFooter />
        </Page>
      )}

      {/* ═══ REMARKS + SIGNATURES (final page) ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />
        <SectionBar title="OVERALL REMARKS & SIGNATURES" />

        {inspection.remarks && (
          <View style={s.remarksBlock}>
            <Text style={s.remarksLabel}>Remark Description</Text>
            <Text style={s.remarksText}>{inspection.remarks}</Text>
          </View>
        )}

        <View style={s.sigRow}>
          <View style={s.sigBox}>
            <Text style={s.sigLabel}>Factory Rep Signature</Text>
            {inspection.factory_rep_signature_url ? (
              <Image src={inspection.factory_rep_signature_url} style={s.sigImg} />
            ) : (
              <View style={s.sigEmpty} />
            )}
            <Text style={s.sigName}>Authorized Signature</Text>
            <Text style={s.sigRole}>Production Head / Factory QC</Text>
          </View>
          <View style={s.sigBox}>
            <Text style={s.sigLabel}>QA Inspector Signature</Text>
            {inspection.inspector_signature_url ? (
              <Image src={inspection.inspector_signature_url} style={s.sigImg} />
            ) : (
              <View style={s.sigEmpty} />
            )}
            <Text style={s.sigName}>{dash(inspection.inspector_name)}</Text>
            <Text style={s.sigRole}>QC Inspector</Text>
          </View>
          <View style={[s.sigBox, { flex: 0.7 }]}>
            <Text style={s.sigLabel}>Inspection Date</Text>
            <View style={{ height: 44, marginBottom: 4, justifyContent: 'flex-end' }}>
              <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold' }}>
                {formatDate(inspection.created_at)}
              </Text>
            </View>
            <Text style={s.sigRole}>Date of Inspection</Text>
          </View>
        </View>

        <View style={{
          position: 'absolute', bottom: 14, left: 36, right: 36,
          flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
          borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
        }}>
          <Text style={{ fontSize: 7.5, color: '#555555', flex: 1, textAlign: 'center' }}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#2563eb' }}>QAMATE</Text>
            <Text style={{ fontSize: 7.5, color: '#555555' }}>info@qamate.in | www.qamate.in</Text>
          </View>
        </View>
      </Page>

    </Document>
  );
};

export default LineInspectionReportPDF;
